import type { CTRL_SCHEMA } from "@repo/sdk/types";
import { useHttp, type HttpConfiguration } from "@repo/utils/hooks";
import { useCtrlClient } from "../contexts/CtrlApiProvider";

export type K8SEmulator = CTRL_SCHEMA["K8SEmulator"];

/**
 * Hook for fetching all emulators using SWR
 *
 * @param config - Optional SWR configuration
 * @returns SWR response with emulators data, error, loading states, and mutation function
 *
 * @example
 * ```tsx
 * function EmulatorList() {
 *   const { data: emulators, error, isLoading, mutate } = useEmulators()
 *
 *   if (isLoading) return <div>Loading...</div>
 *   if (error) return <div>Error: {error.message}</div>
 *
 *   return (
 *     <ul>
 *       {emulators?.map(emu => (
 *         <li key={emu.id}>{emu.id} - {emu.status}</li>
 *       ))}
 *     </ul>
 *   )
 * }
 * ```
 */
export function useEmulators(config?: HttpConfiguration) {
  const client = useCtrlClient();

  return useHttp<K8SEmulator[]>(
    "emulators",
    async () => {
      return await client.getEmulators();
    },
    config
  );
}
